import './main.less';
import { actions, selectors } from './';
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Link } from 'react-router';
import renderViewsState from '../../shared/render-views-state';

import { selectors as rootSelectors } from '../../root';

import ProfileHeader from '../components/profile/header/main';
import Timeline from '../components/profile/timeline/main';
import DashboardVisits from '../components/profile/dashboard-visits/main';
import Notes from '../components/profile/notes/main';
import Search from '../../shared/search/main';
import ParamsForm from '../components/profile/forms/params/main';
import AffiliateTabStatusesForm from '../components/profile/affiliates/statuses';
import ScrollButton from '../components/profile/scroll-button/main';

import Calendar from '../../shared/calendar/main';
import { Tabs, Pane } from '../../shared/tabs';

import IndividualTermsForm from '../components/profile/forms/individual-terms/main';
import PayoutSettingsForm from '../components/profile/forms/payout-settings/main';
import AlertsForm from '../components/profile/forms/alerts/main';
import Survey from '../components/profile/survey/main';

import createThreadsForm from '../../threads/components/create-form/main';
import createPayoutForm from '../../payouts/components/forms/create-payout/main';
import createTransactionForm from '../../payouts/components/forms/transaction/main';
import createPointTransactionForm from '../components/points-transaction-form/main';
import createAddOfferFromAffiliateForm from '../components/add-offer-from-affiliate-form/main';
import createIncreaseApproveForm from '../components/increase-approve-form/main';
import createRequestGarantForm from '../components/request-garant-form/main';
import approveDailyForm from '../components/approve-daily-form/main';
import createTransferBalanceAndAccumulatedPointsForm
	from '../components/create-transfer-balance-and-accumulated-points-form/main';
import Attachments from '../components/profile/forms/attachments/main';
import Affiliates from '../components/profile/affiliates/main';

const CreateThreadsForm = createThreadsForm('affiliateProfileCreateThreadForm');
const CreatePayoutForm = createPayoutForm('affiliateProfileCreatePayoutForm');
const TransactionForm = createTransactionForm('affiliateProfileTransactionForm');
const AddOfferFromAffiliateForm = createAddOfferFromAffiliateForm('addOfferFromAffiliateForm');
const PointTransactionForm = createPointTransactionForm('affiliateProfilePointTransactionForm');
const IncreaseApproveForm = createIncreaseApproveForm('affiliateProfileIncreaseApproveForm');
const RequestGarantForm = createRequestGarantForm('affiliateProfileIncreaseApproveForm');
const ApproveDailyForm = approveDailyForm('approveDailyForm');
const TransferBalanceAndAccumulatedPointsForm =
	createTransferBalanceAndAccumulatedPointsForm('transferBalanceAndAccumulatedPointsForm');

const isRocket = process.env.CPA_ENV === 'rocket';

const Main = renderViewsState(
	props => (
		<div className="container__content affiliate-profile">
			<div className="container__content-title">
				<div className="container__content-title-position">
					<Link to="affiliates/" className="link">
						{props.intl('Return to list of affiliates')}
					</Link>
				</div>
			</div>
			<div className="container__content-body">
				<ProfileHeader {...props} />
				<div className="profile-body">
					<div className="content-block">
						<Tabs selected={props.currentTab} onTabChange={props.changeTab}>
							<Pane label={props.intl('Timeline')} index="timeline">
								<Timeline {...props} />
							</Pane>
							<Pane
								label={props.intl('Offers')}
								index="individual-terms"
								isVisible={props.can('offersGrant') && props.can('viewOffers')}
							>
								<IndividualTermsForm
									{...props}
									{...props.individualTermsFormData}
									enableReinitialize={true}
									keepDirtyOnReinitialize={true}
								/>
							</Pane>
							<Pane
								label={props.intl('Payout settings')}
								index="payout-settings"
								isVisible={props.can('payoutsCreate')}
							>
								<PayoutSettingsForm />
							</Pane>
							<Pane label={props.intl('Files')} index="files">
								<Attachments {...props} />
							</Pane>

							<Pane
								label={props.intl('Alerts')}
								index="individual-alerts"
								isVisible={true}
							>
								<AlertsForm
									{...props}
									{...props.individualAlertsFormData}
									enableReinitialize={true}
									keepDirtyOnReinitialize={false}
								/>
							</Pane>

							<Pane
								label={props.intl('Survey')}
								index="survey"
								isVisible={true}
							>
								<Survey
									survey={props.data.survey}
									intl={props.intl}
								/>
							</Pane>
							<Pane
								label={props.intl('Notes')}
								index="notes"
								isVisible={!isRocket}
							>
								<Notes {...props}/>
							</Pane>
							<Pane
								label={props.intl('Visits')}
								index="dashboard-visits"
								isVisible={isRocket}
							>
								<DashboardVisits
									actionsSwitchControlMultiItems={props.dashboardVisitActionsSwitchItems}
									switchActions={props.switchDashboardVisitsActions}
									calendar={{
										...props.dashboardVisitCalendar,
										calendarApply: props.dashboardVisitCalendarApply,
										calendarChange: props.dashboardVisitCalendarChange,
										calendarInit: props.dashboardVisitCalendarInit,
										calendarSetUserTimezoneDate: props.dashboardVisitCalendarSetUserTimezoneDate,
										calendarToggle: props.dashboardVisitCalendarToggle
									}}
									data={props.dashboardSignupVisitData}
									rows={props.getDashboardVisits.rows}
									total={props.getDashboardVisits.total}
									apply={props.fetchDataDashboardVisits}
									showMore={props.showMoreDashboardVisits}
								/>
							</Pane>

							{props.data &&
								props.data.is_master && (
								<Pane
									label={props.intl('Affiliates')}
									index="affiliates"
									count={props.totalChildAffiliatesAmount || null}
									isVisible={props.can('view')}
								>
									<Affiliates
										{...props}
										{...props.childAffiliatesFormData}
									/>
								</Pane>
							)}
						</Tabs>
					</div>
					{props.currentTab === 'timeline' && (
						<div className="controls-block">
							<Search
								{...props}
								form="affiliateProfileSearchForm"
								action={props.refetchLogs}
								initialValues={props.searchInitialValue}
							/>

							<Calendar
								{...props.calendar}
								onInit={props.calendarInit}
								onToggle={props.calendarToggle}
								onChange={props.calendarChange}
								onApply={props.calendarApply}
								intl={props.intl}
							/>
							<ParamsForm {...props} initialValues={props.paramsInitialValues} />

							<div className="buttons-group">
								<button className="ui-btn ui-btn-primary apply" onClick={props.apply}>
									{props.intl('Apply')}
								</button>
								<button className="ui-btn ui-btn-secondary clear" onClick={props.clear} />
							</div>
						</div>
					)}
					{props.currentTab === 'affiliates' && (
						<div className="controls-block">
							<Search
								{...props}
								form="affiliateProfileAffiliatesTabSearchForm"
								action={props.applyAffiliatesTab}
								initialValues={props.childAffiliatesFormData.searchFormInitialValue}
								destroyOnUnmount={false}
							/>

							<AffiliateTabStatusesForm
								{...props}
								initialValues={props.childAffiliatesFormData.statusesFormInitialValues}
								destroyOnUnmount={false}
							/>

							<div className="buttons-group">
								<button className="ui-btn ui-btn-primary apply" onClick={props.applyAffiliatesTab}>
									{props.intl('Apply')}
								</button>
								<button className="ui-btn ui-btn-secondary clear" onClick={props.clearAffiliatesTab} />
							</div>
						</div>
					)}
				</div>
				{props.can('createThreads') && (
					<CreateThreadsForm {...props} initialValues={props.createThreadFormInitialValues} />
				)}
				{props.can('payoutsCreate') && (
					<CreatePayoutForm {...props} initialValues={props.createPayoutFormData.initialValues} />
				)}
				{props.can('createTransaction') && (
					<TransactionForm {...props} initialValues={props.transactionFormData.initialValues} />
				)}
				{props.can('createTransaction') && (
					<AddOfferFromAffiliateForm {...props} initialValues={props.transactionFormData.initialValues} />
				)}
				{props.can('createThreads') && (
					<PointTransactionForm
						{...props}
						initialValues={props.pointTransactionFormData.initialValues}
					/>
				)}
				{props.can('conversionsPastManualApprove') && (
					<IncreaseApproveForm
						{...props}
						initialValues={props.pointTransactionFormData.initialValues}
					/>
				)}
				{props.can('garantApproveRequestsView') && (
					<RequestGarantForm
						{...props}
						initialValues={props.requestGarantFormData.initialValues}
					/>
				)}
				{props.can('conversionsApproveDaily') && (
					<ApproveDailyForm
						{...props}
						initialValues={props.approveDailyFormInitialData}
					/>
				)}
				{isRocket && props.can('transferBalanceAndAccumulatedPoints') && (
					<TransferBalanceAndAccumulatedPointsForm
						initialValues={props.transferBalanceFormData.initialValues}
						isFormOpened={props.transferBalanceShouldShowStatus}
						toggleForm={props.toggleTransferBalanceAndAccumulatedPointsForm}
						submitForm={props.submitTransferBalanceAndAccumulatedPointsForm}
						affiliatesLookups={props.affiliatesLookups}
					/>
				)}
				<ScrollButton />
			</div>
		</div>
	),
	'view'
);

Main.propTypes = {
	affiliateID: PropTypes.number
};

export default connect(
	state => ({
		data: selectors.getData(state),
		affiliateID: selectors.getAffiliateID(state),
		logs: selectors.getLogs(state),
		notes: selectors.getNotes(state),
		priorities: selectors.getPrioritiesList(state),
		calendar: selectors.getCalendar(state),
		affiliateApproveIncreaseCalendar: selectors.affiliateApproveIncreaseCalendar(state),
		affiliateTransactionCalendar: selectors.getAffiliateTransactionCalendar(state),
		garantRequestCalendar: selectors.getGarantRequestCalendar(state),

		payoutCurrencies: selectors.getPayoutCurrencies(state),
		campaignPayouts: selectors.getCampaignPayouts(state),

		searchInitialValue: selectors.getSearchInitialValue(state),
		paramsInitialValues: selectors.getParamsInitialValues(state),

		shouldShowCreateThreadForm: selectors.getShouldShowCreateThreadFormStatus(state),
		attachmentsInfo: selectors.getAttachmentsInfo(state),
		createThreadFormInitialValues: selectors.getCreateThreadFormInitialValues(state),
		createThreadFormDisabledFields: selectors.getCreateThreadFormDisabledFields(state),

		createPayoutFormData: selectors.getCreatePayoutFormData(state),
		createPayoutShouldShowStatus: selectors.getCreatePayoutShouldShowStatus(state),

		transactionFormData: selectors.getTransactionFormData(state),
		transactionShouldShowStatus: selectors.getTransactionShouldShowStatus(state),
		transactionFormType: selectors.getTransactionFormType(state),
		transactionFormExchangeRate: selectors.getTransactionFormExchangeRate(state),

		selectedCurrenciesPanIndexMap: selectors.getSelectedCurrenciesPanIndexMap(state),

		transferBalanceFormData: selectors.getTransferBalanceFormData(state),
		transferBalanceShouldShowStatus: selectors.getTransferBalanceShouldShowStatus(state),

		pointTransactionFormData: selectors.getPointTransactionFormData(state),
		isAddOfferFromAffiliateFormVisible: selectors.getIsAddOfferFromAffiliateFormVisible(state),
		isApproveDailyFormVisible: selectors.getIsApproveDailyFormVisible(state),
		dailyApprovesList: selectors.getDailyApprovesList(state),
		pointTransactionShouldShowStatus: selectors.getPointTransactionShouldShowStatus(state),
		increaseApproveFormVisibility: selectors.getIncreaseApproveFormVisibility(state),
		isRequestGarantFormVisible: selectors.getRequestGarantFormVisibility(state),
		isBlockedSubmitButton: selectors.getIsBlockedSubmitButton(state),
		isBlockedRequestGarantSubmitButton: selectors.getIsBlockedRequestGarantSubmitButton(state),
		increaseApproveFormOfferApproveData: selectors.getIncreaseApproveFormOfferApproveData(state),
		requestGarantFormOfferApproveData: selectors.getRequestGarantFormOfferApproveData(state),
		increaseApproveFormData: selectors.getIncreaseApproveFormData(state),
		requestGarantFormData: selectors.getRequestGarantFormData(state),

		individualTermsFormData: selectors.getIndividualTermsFormData(state),
		individualTermsFilters: selectors.getIndividualTermsFilters(state),
		approveDailyFormData: selectors.getApproveDailyFormData(state),
		approveDailyFormInitialData: selectors.getApproveDailyFormInitialData(state),
		individualAlertsFormData: selectors.getIndividualAlertsFormData(state),

		affiliateFilterQuery: selectors.getAffiliateFilterQuery(state),
		newsTypesList: selectors.getNewsTypesList(state),
		notificationEventTypesList: selectors.getNotificationEventTypesList(state),

		currentTab: selectors.getCurrentTab(state),
		showCommentSubmitBtn: selectors.getCommentSubmitBtn(state),

		menuConfig: rootSelectors.getMenuConfig(state),

		availableCurrencies: selectors.getAvailableCurrencies(state),

		payoutMatrix: selectors.getPayoutMatrix(state),
		offers: selectors.getOffers(state),

		attachments: selectors.getAttachments(state),
		childAffiliates: selectors.getChildAffiliates(state),
		childAffiliatesAmount: selectors.getChildAffiliatesAmount(state),
		totalChildAffiliatesAmount: selectors.getTotalChildAffiliatesAmount(state),
		childAffiliatesFormData: selectors.getChildAffiliatesFormData(state),

		dashboardSignupVisitData: selectors.getDashboardSignupVisitData(state),
		getDashboardVisits: selectors.getDashboardVisits(state),
		dashboardVisitActionsSwitchItems: selectors.getDashboardVisitActionsSwitchItems(state),
		dashboardVisitCalendar: selectors.getDashboardVisitCalendar(state),

		intl: selectors.getIntl(state),
		can: selectors.getCan(state),
		progressBar: selectors.getProgressBar(state),
		error: selectors.getError(state)
	}),
	dispatch => ({
		...bindActionCreators(actions, dispatch),
		individualTermsFilters: bindActionCreators(actions.individualTermsFiltersActions, dispatch)
	}),
	(stateProps, dispatchProps, ownProps) => ({
		...ownProps,
		...stateProps,
		...dispatchProps,
		individualTermsFilters: {
			...stateProps.individualTermsFilters,
			...dispatchProps.individualTermsFilters
		}
	})
)(Main);



// WEBPACK FOOTER //
// ./frontend/admin-dashboard/src/modules/affiliates/profile/main.js